// @ts-nocheck -- verbatim TAPP payroll UI compatibility boundary
import { forwardRef, type InputHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { SearchField as HeroSearchField } from "@heroui/react";
import { clsx } from "clsx";

const baseClasses = [
  "w-full rounded-lg border border-border bg-surface",
  "px-3 py-2 text-sm text-foreground placeholder-foreground-subtle",
  "transition-colors duration-150",
  "hover:border-border-strong",
  "focus:border-accent focus:outline-none",
  "disabled:opacity-50 disabled:cursor-not-allowed",
].join(" ");

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, leftIcon, rightIcon, className, id, ...props }, ref) => {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, "-");
    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label
            htmlFor={inputId}
            className="text-sm font-medium text-foreground"
          >
            {label}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-subtle">
              {leftIcon}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            className={clsx(
              baseClasses,
              leftIcon && "pl-9",
              rightIcon && "pr-9",
              error && "border-danger focus:border-danger",
              className,
            )}
            aria-invalid={error ? "true" : undefined}
            aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
            {...props}
          />
          {rightIcon && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-subtle">
              {rightIcon}
            </span>
          )}
        </div>
        {error && (
          <p id={`${inputId}-error`} className="text-xs text-danger" role="alert">
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={`${inputId}-hint`} className="text-xs text-foreground-subtle">
            {hint}
          </p>
        )}
      </div>
    );
  },
);
Input.displayName = "Input";

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, hint, className, id, ...props }, ref) => {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, "-");
    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label htmlFor={inputId} className="text-sm font-medium text-foreground">
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={inputId}
          className={clsx(
            baseClasses,
            "resize-none leading-relaxed",
            error && "border-danger focus:border-danger",
            className,
          )}
          aria-invalid={error ? "true" : undefined}
          {...props}
        />
        {error && (
          <p className="text-xs text-danger" role="alert">{error}</p>
        )}
        {hint && !error && (
          <p className="text-xs text-foreground-subtle">{hint}</p>
        )}
      </div>
    );
  },
);
Textarea.displayName = "Textarea";

export function SearchInput({
  value,
  onValueChange,
  label,
  placeholder,
  className,
}: {
  value: string;
  onValueChange: (value: string) => void;
  label: string;
  placeholder?: string;
  className?: string;
}) {
  return (
    <HeroSearchField.Root
      aria-label={label}
      value={value}
      onChange={onValueChange}
      fullWidth
      variant="secondary"
      className={className}
    >
      <HeroSearchField.Group>
        <HeroSearchField.SearchIcon />
        <HeroSearchField.Input placeholder={placeholder} />
        <HeroSearchField.ClearButton />
      </HeroSearchField.Group>
    </HeroSearchField.Root>
  );
}
